import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "@/redux/store";
import { getCookie } from "@/utils";
import {
  fetchUserDetails,
  getPasswordPolicy,
  recoverPassword,
  updateUser
} from "@/redux/apps/users/UserSlice";
import { showMoreMessage, showSuccessMessage } from "@/redux/apps/toast/ToastSlice";
import { ScreenMapping, ToastMessages } from "@/libs/ToastMessages";
import { validatePhoneNumberGoogle } from "@/utils/helpers";

export const useAccountSettings = () => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [passwordValue, setPasswordValue] = useState("password");
  const [showPassMngt, setShowPassMngt] = useState(false);
  const [errors, setErrors] = useState(false);
  const domainName = getCookie("xsm_user_domain");
  const [resetLinkSend, setResetLinkSend] = useState(false);
  const loggedUser = getCookie("xsm_user_details")
    ? JSON.parse(getCookie("xsm_user_details") || "")
    : null;
  const loggedUserName = loggedUser?.user_name ?? "";
  const [schema, setSchema] = useState();

  const generateValidationSchema = (regexPattern: any, regexText?: any) => {
    const regexObject = new RegExp(
      regexPattern?.slice(1, -1) || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/
    );
    const regexValidationText =
      regexText ||
      "Should have at least one Upper case and one Lower Case and Should have at least one Number";
    return Yup.object().shape({
      first_name: Yup.string()
        .required("First Name is Required")
        .min(3, "First Name Must be greater than 3 letters")
        .max(25, "First Name Must be less than 25 letters"),
      last_name: Yup.string()
        .required("Last Name is Required")
        .min(3, "Last Name Must be greater than 3 letters")
        .max(25, "Last Name Must be less than 25 letters"),
      email_address: Yup.string().email("Invalid email").required("Email is Required"),
      mobile_number: Yup.string().required("Phone number is Required"),
      password: Yup.string()
        .required("Password is Required")
        .matches(regexObject, regexValidationText),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm Password is Required")
        .matches(regexObject, regexValidationText)
    });
  };

  const getDynamicSchema = async () => {
    const response: any = await dispatch(getPasswordPolicy({ domainName }));
    const regex = response.payload?.result?.pass_reg_expression;
    const regexText = response.payload?.result?.pass_regex_message.join("");
    return generateValidationSchema(regex, regexText);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (document.URL.includes("account-settings")) {
        try {
          const finSchema: any = await getDynamicSchema();
          setSchema(finSchema);
        } catch (error) {
          // Handle errors here if necessary
        }
      }
    };

    fetchData();
    fetchUserData();
  }, []);

  async function fetchData() {
    const finSchema: any = await getDynamicSchema();
    setSchema(finSchema);
  }

  const accountSettingFormik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email_address: "",
      mobile_number: "",
      country_code: "+91",
      password: "",
      confirm_password: ""
    },
    onSubmit: () => {},
    validationSchema: schema
  });

  const { values, setFormikState }: any = accountSettingFormik;

  const validatePhoneNumber = () => {
    const isValid = validatePhoneNumberGoogle(values.country_code, values.mobile_number);
    return !isValid;
  };

  const valid = !validatePhoneNumber();

  async function fetchUserData() {
    const response = await dispatch(
      fetchUserDetails({ domainName, userName: loggedUser?.email_address })
    );
    const userDetails = response.payload?.result?.users[0];
    const phone = userDetails?.mobile_number.split(" ");
    setFormikState((state: any) => ({
      ...state,
      values: {
        ...state.values,
        ...userDetails,
        mobile_number: phone?.length > 1 ? phone[1] : phone?.length > 0 ? phone[0] : "",
        country_code: phone?.length > 1 ? phone[0] : "+91"
      }
    }));
    return response;
  }

  const handleSubmit = async (link?: any) => {
    console.log("called", link, accountSettingFormik, values);
    const getDisable = () => {
      if (!link) {
        return !valid;
      } else {
        return !(accountSettingFormik?.isValid && accountSettingFormik?.dirty);
      }
    };
    accountSettingFormik.validateForm().then(async (err: any) => {
      if (!getDisable()) {
        const data = {
          dom_name: domainName,
          user_mobile_number: `${values.country_code} ${values.mobile_number}`,
          user_password: link ? values.password : null,
          modified_by: loggedUserName,
          status: values.status,
          userId: values?.user_uuid,
          user_last_name: values?.last_name,
          user_first_name: values?.first_name
        };

        console.log(data, "data");

        const response: any = await dispatch(updateUser(data));
        if (response.payload?.isSuccess) {
          await fetchUserData();
          setEditMode(false);
          setShowPassMngt(false);
          dispatch(
            showSuccessMessage(
              ToastMessages.UPDATED.replace(":screen", ScreenMapping.USER).replace(
                ":name",
                values.first_name
              )
            )
          );
        } else {
          dispatch(
            showMoreMessage({
              ...response.payload,
              message: ToastMessages.FAILED_UPDATED.replace(":screen", ScreenMapping.USER)
            })
          );
        }
      } else {
        setErrors(true);
      }
    });
  };

  const handleResetPassword = () => {
    if (passwordValue === "passwordLink") {
      const data = {
        email: loggedUser?.email_address
      };
      const response: any = dispatch(recoverPassword(data));
      response.then((response: any) => {
        if (response.payload?.isSuccess) {
          dispatch(showSuccessMessage(response.payload?.message));
          setResetLinkSend(true);
        } else {
          dispatch(showMoreMessage(response.payload));
        }
      });
    } else {
      handleSubmit(true);
    }
  };

  return {
    accountSettingFormik,
    editMode,
    setEditMode,
    passwordValue,
    setPasswordValue,
    values: accountSettingFormik.values,
    setShowPassMngt,
    showPassMngt,
    loggedUser,
    errors,
    setErrors,
    valid,
    handleSubmit,
    handleResetPassword,
    resetLinkSend
  };
};
